/**
 * @quotech/ui - Universal Keyboard Shortcuts Tab
 * 
 * PATH: packages/ui/src/components/settings/KeyboardShortcutsTab.tsx
 * PURPOSE: Display keyboard shortcuts for the current hub
 */

import { Keyboard, Command, Search, Settings, Home, ArrowLeft, ArrowRight, Plus } from 'lucide-react'
import { cn } from '../../lib/utils'

// =============================================================================
// TYPES
// =============================================================================

export interface KeyboardShortcut {
  /** Display label */
  label: string
  /** Keys array (e.g., ['⌘', 'K']) */
  keys: string[]
  /** Category for grouping */
  category?: string
  /** Optional description */
  description?: string
}

export interface KeyboardShortcutsTabProps {
  /** List of shortcuts specific to this hub */
  shortcuts?: KeyboardShortcut[]
  /** Whether to show universal shortcuts */
  showUniversal?: boolean
  /** Hub name for display */
  hubName?: string
  /** Custom className */
  className?: string
}

// =============================================================================
// UNIVERSAL SHORTCUTS
// =============================================================================

const UNIVERSAL_SHORTCUTS: KeyboardShortcut[] = [
  { label: 'Open Settings', keys: ['⌘', ','], category: 'General' },
  { label: 'Global Search', keys: ['⌘', 'F'], category: 'General' },
  { label: 'Search', keys: ['⌘', '/'], category: 'General' },
  { label: 'Go Home', keys: ['⌘', 'H'], category: 'Navigation' },
  { label: 'Go Back', keys: ['⌘', '['], category: 'Navigation' },
  { label: 'Go Forward', keys: ['⌘', ']'], category: 'Navigation' },
  { label: 'Close Modal', keys: ['Esc'], category: 'General' },
]

// =============================================================================
// SHORTCUT KEY COMPONENT
// =============================================================================

interface ShortcutKeyProps {
  children: string
}

function ShortcutKey({ children }: ShortcutKeyProps) {
  return (
    <kbd className={cn(
      'inline-flex items-center justify-center min-w-[24px] h-6 px-1.5',
      'bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600',
      'rounded text-xs font-medium text-slate-600 dark:text-slate-300',
      'shadow-sm'
    )}>
      {children}
    </kbd>
  )
}

// =============================================================================
// SHORTCUT ROW COMPONENT
// =============================================================================

interface ShortcutRowProps {
  shortcut: KeyboardShortcut
}

function ShortcutRow({ shortcut }: ShortcutRowProps) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div>
        <span className="text-sm font-medium text-slate-900 dark:text-white">
          {shortcut.label}
        </span>
        {shortcut.description && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {shortcut.description}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        {shortcut.keys.map((key, i) => (
          <span key={i} className="flex items-center">
            <ShortcutKey>{key}</ShortcutKey>
            {i < shortcut.keys.length - 1 && (
              <span className="mx-0.5 text-slate-400">+</span>
            )}
          </span>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function KeyboardShortcutsTab({
  shortcuts = [],
  showUniversal = true,
  hubName,
  className,
}: KeyboardShortcutsTabProps) {
  // Combine and group shortcuts
  const allShortcuts = showUniversal ? [...UNIVERSAL_SHORTCUTS, ...shortcuts] : shortcuts
  
  // Group by category
  const groupedShortcuts = allShortcuts.reduce((acc, shortcut) => {
    const category = shortcut.category || 'Other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(shortcut)
    return acc
  }, {} as Record<string, KeyboardShortcut[]>)

  // Define category order
  const categoryOrder = ['General', 'Navigation', hubName || 'Hub Specific', 'Other']
  const sortedCategories = Object.keys(groupedShortcuts).sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a)
    const bIndex = categoryOrder.indexOf(b)
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b)
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header info */}
      <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
        <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center">
          <Keyboard className="w-5 h-5 text-teal-500" />
        </div>
        <div>
          <p className="font-medium text-slate-900 dark:text-white">
            Keyboard Shortcuts
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Use these shortcuts to navigate faster
          </p>
        </div>
      </div>

      {/* Shortcuts by category */}
      {sortedCategories.map((category) => (
        <div key={category}>
          <h4 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
            {category}
          </h4>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
            {groupedShortcuts[category].map((shortcut, i) => (
              <div key={i} className="px-4">
                <ShortcutRow shortcut={shortcut} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {allShortcuts.length === 0 && (
        <div className="text-center py-12">
          <Keyboard className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="font-medium text-slate-900 dark:text-white mb-2">
            No shortcuts available
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Keyboard shortcuts for this hub will appear here.
          </p>
        </div>
      )}

      {/* Tip */}
      <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
        <span className="inline-flex items-center gap-1">
          Tip: Use <ShortcutKey>⌘</ShortcutKey><span>+</span><ShortcutKey>K</ShortcutKey> to open the command palette
        </span>
      </div>
    </div>
  )
}

export default KeyboardShortcutsTab
